import { useState, useEffect } from "react";
import { Calendar, MapPin, Users, Check, Mail, Phone, Globe, Instagram, Download } from "lucide-react";
import { EVENT, SPONSOR_TIERS, TRIBUTE_TIER, DAY_INCLUDES, CONTACT, SPONSOR_DETAILS_VISIBLE } from "@/data/content";

// Print-ready one-page marketing flyer (8.5 x 11 ratio). Route: /flyer
const SHEET_W = 816;
const SHEET_H = 1056;

export default function Flyer() {
  const [scale, setScale] = useState(1);

  useEffect(() => {
    const compute = () => {
      const gutter = window.innerWidth < 640 ? 16 : 48;
      const avail = window.innerWidth - gutter * 2;
      setScale(Math.min(1, avail / SHEET_W));
    };
    compute();
    window.addEventListener("resize", compute);
    return () => window.removeEventListener("resize", compute);
  }, []);

  return (
    <div className="w-full flex justify-center bg-neutral-800 py-8 px-4 sm:px-0 print:p-0 print:bg-white overflow-hidden">
      <style>{`
        @media print {
          @page { size: letter; margin: 0; }
          html, body { margin: 0 !important; padding: 0 !important; background: #fff !important; }
          .no-print { display: none !important; }
          .flyer-scale-wrap { width: auto !important; height: auto !important; }
          [data-testid="flyer-sheet"] { transform: none !important; box-shadow: none !important; break-inside: avoid; page-break-inside: avoid; }
        }
        [data-testid="flyer-sheet"], [data-testid="flyer-sheet"] * {
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
      `}</style>

      <a
        href="/heroes-legacy-golf-classic-flyer.pdf"
        download
        data-testid="flyer-print-btn"
        className="no-print fixed top-4 right-4 sm:top-6 sm:right-6 z-50 label-font text-[11px] sm:text-xs bg-rust text-primary-foreground px-4 py-2.5 sm:px-5 sm:py-3 border border-espresso flex items-center gap-2 hard-shadow-sm hover:-translate-y-0.5 transition-transform"
      >
        <Download size={15} /> Download Flyer (PDF)
      </a>

      <div className="flyer-scale-wrap" style={{ width: SHEET_W * scale, height: SHEET_H * scale }}>
        <div
          data-testid="flyer-sheet"
          className="relative bg-lightcream text-espresso paper-noise overflow-hidden"
          style={{ width: `${SHEET_W}px`, height: `${SHEET_H}px`, padding: "38px 50px", transform: `scale(${scale})`, transformOrigin: "top left" }}
        >
        {/* Outer frame */}
        <div className="absolute inset-4 border border-espresso/40 pointer-events-none" />
        <div className="absolute inset-[22px] border-2 border-rust/70 pointer-events-none" />

        <div className="relative z-10 flex flex-col items-center text-center">
          {/* Overline */}
          <div className="label-font text-[12px] text-rust tracking-[0.3em]">
            ★ &nbsp; INAUGURAL EDITION · MONTGOMERY COUNTY, TEXAS &nbsp; ★
          </div>

          {/* Logo */}
          <img src="/flat/badge-on-lightcream.jpg" alt="The Wayne Walker Heroes & Legacy Golf Classic" className="w-40 h-40 object-contain mt-2" />

          {/* Title */}
          <h1 className="leading-[0.95] mt-2 uppercase" style={{ fontFamily: "'Oswald', sans-serif", fontWeight: 700, fontSize: "56px", letterSpacing: "0.01em" }}>
            The Heroes & Legacy
          </h1>
          <h1 className="text-rust leading-[0.95] uppercase" style={{ fontFamily: "'Oswald', sans-serif", fontWeight: 700, fontSize: "56px", letterSpacing: "0.01em" }}>
            Golf Classic
          </h1>

          <div className="flex items-center gap-3 mt-4">
            <span className="h-px w-16 bg-rust" />
            <span className="text-rust">★</span>
            <span className="h-px w-16 bg-rust" />
          </div>

          <p className="mt-4 text-espresso/80 uppercase" style={{ fontFamily: "'Oswald', sans-serif", fontWeight: 600, fontSize: "15px", letterSpacing: "0.06em" }}>
            Benefiting the Wayne Walker Foundation
          </p>

          {/* WHEN / WHERE / WHO */}
          <div className="grid grid-cols-3 gap-4 w-full mt-6">
            {[
              { icon: Calendar, label: "When", main: EVENT.dateLabel, sub: EVENT.dayLabel },
              { icon: MapPin, label: "Where", main: EVENT.venue, sub: EVENT.venueSub },
              { icon: Users, label: "Who", main: EVENT.teams, sub: EVENT.teamsSub },
            ].map((s) => (
              <div key={s.label} className="border border-espresso/30 bg-cream/60 py-4 px-3">
                <div className="flex items-center justify-center gap-1.5 text-rust">
                  <s.icon size={13} /><span className="label-font text-[10px]">{s.label}</span>
                </div>
                <div className="font-serif font-bold mt-1" style={{ fontSize: "19px" }}>{s.main}</div>
                <div className="text-[11px] text-espresso/70 mt-0.5 leading-tight">{s.sub}</div>
              </div>
            ))}
          </div>

          {/* Two columns: The Day / Sponsorship */}
          <div className="grid grid-cols-2 gap-6 w-full mt-6 text-left">
            <div>
              <div className="bg-espresso text-cream label-font text-[11px] py-2 px-3 text-center">The Day Includes</div>
              <ul className="mt-3 space-y-2">
                {DAY_INCLUDES.map((d) => (
                  <li key={d} className="flex items-start gap-2 text-[13px] leading-snug">
                    <Check size={14} className="text-rust shrink-0 mt-0.5" /> {d}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <div className="bg-espresso text-cream label-font text-[11px] py-2 px-3 text-center">Sponsorship Tiers</div>
              <ul className="mt-3 space-y-2">
                {[...SPONSOR_TIERS, TRIBUTE_TIER].map((t) => (
                  <li key={t.key} className="flex items-baseline justify-between gap-2 text-[13px] border-b border-dashed border-espresso/30 pb-1.5">
                    <span className="label-font text-[12px] text-espresso">{t.name}</span>
                    <span className="font-serif font-bold text-rust">{SPONSOR_DETAILS_VISIBLE ? `$${t.price.toLocaleString()}` : "Coming Soon"}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* CTA bar */}
          <div className="w-full mt-7 border-2 border-espresso bg-espresso text-cream">
            <div className="label-font text-[11px] text-rust tracking-[0.2em] text-center py-2 border-b border-cream/20">
              ★ Become a Sponsor · Register a Team · Join the Mission ★
            </div>
            <div className="grid grid-cols-3 text-center divide-x divide-cream/20">
              <div className="py-4 px-3">
                <div className="font-serif" style={{ fontSize: "16px" }}>Sponsorship</div>
                <div className="text-[12px] text-cream/80 mt-1 flex items-center justify-center gap-1"><Mail size={11} /> {CONTACT.sponsorEmail}</div>
                <div className="text-[12px] text-cream/80 flex items-center justify-center gap-1"><Phone size={11} /> {CONTACT.sponsorPhone}</div>
              </div>
              <div className="py-4 px-3">
                <div className="font-serif" style={{ fontSize: "16px" }}>Registration</div>
                <div className="text-[12px] text-cream/80 mt-1">$250 individual</div>
                <div className="text-[12px] text-cream/80">$1,000 team</div>
              </div>
              <div className="py-4 px-3">
                <div className="font-serif" style={{ fontSize: "16px" }}>Online</div>
                <div className="text-[12px] text-cream/80 mt-1 flex items-center justify-center gap-1"><Globe size={11} /> {CONTACT.website}</div>
                <div className="text-[12px] text-cream/80 flex items-center justify-center gap-1"><Instagram size={11} /> {CONTACT.social}</div>
              </div>
            </div>
          </div>

          <div className="label-font text-[10px] text-espresso/50 tracking-[0.25em] mt-4">
            The Wayne Walker Heroes & Legacy Golf Classic · Est. 2027
          </div>
        </div>
      </div>
      </div>

    </div>
  );
}
